/**
 * The third enrolment trigger: the first character a member types into a blank.
 *
 * WHY THERE IS A THIRD. The encryption spec names two — the Notes Hub open and a
 * highlight save — and gives no reason for leaving blanks out. A member who
 * reads the lessons and fills in the answers but never opens the Hub and never
 * highlights anything passes every eligibility gate and still never enrols, so
 * everything they write stays in the clear indefinitely. Blanks and open
 * responses are the most-used writing surface in Next Steps; leaving them out
 * excluded exactly the members who use it most.
 *
 * WHY TYPING, AND NOT OPENING THE LESSON OR TAPPING THE FIELD. Enrolment has to
 * follow real content. An enrolled member with an empty vault still meets device
 * approval on their next phone and still carries a recovery code — for nothing.
 * Every Next Steps lesson has blanks (5 to 58 of them), so a lesson-open trigger
 * would have enrolled every reader on their first page; focus would enrol
 * anyone who tapped a field and thought better of it. A non-empty answer is
 * content, and the caller checks that before calling here.
 *
 * NOT AWAITED, ANYWHERE. The save does not wait for this — the write goes up
 * plaintext at the engine's cadence if it gets there first, and either the
 * migration re-seals it or the API refuses it 400 and the retry seals it. Both
 * orderings are correct. What the member must never see is a keystroke waiting
 * on a round trip.
 *
 * Where each half is pinned: the 400's reclassification in `policy.spec.js`, the
 * retry coming back as an envelope in `autosave-outbox-composition.spec.js`, and
 * the window where the member is enrolled but this device has not promoted its
 * key yet — which used to refuse the write outright — in `sealed-writes.spec.js`.
 */
import { enrolIfEligible } from './enrolment';
import { scheduleMigration } from './migration-schedule';

/** The jitter lives in `migration-schedule.js` now; the names are kept for its callers. */
export { MIGRATION_JITTER_MIN_MS, MIGRATION_JITTER_SPREAD_MS } from './migration-schedule';

/**
 * Members this session has already asked about. Once per member, because
 * `upsertBlankAnswer` runs on every keystroke and `enrolIfEligible` is only
 * cheap — one getter read for an enrolled member, a gate check for one the
 * gates refuse — rather than free.
 *
 * A courtesy, not a correctness need: the enrolment itself is single-flight per
 * member and idempotent. Which is why forgetting to clear this could never
 * enrol anyone twice; it would only stop a member enrolling after a sign-out and
 * back in on the same phone, which is what `forgetFirstBlankTriggers` is for.
 */
const asked = new Set();

/** Sign-out, and the specs. `Logout.js` calls this beside `forgetAllRevisions`. */
export function forgetFirstBlankTriggers() {
  asked.clear();
}

/**
 * Ask for an enrolment because the member has just written something into a
 * blank. Never rejects, and never returns anything the caller should wait for.
 *
 * @param {{ store: object, memberId: number|null|undefined }} args
 * @returns {boolean} whether this call was the one that asked
 */
export function enrolOnFirstBlank({ store, memberId } = {}) {
  if (!store) return false;
  // A member id is not required to enrol — `enrolIfEligible` reads the one the
  // key module knows — but it IS what makes "once per session" mean anything.
  // Without one, every keystroke would ask.
  if (!Number.isInteger(memberId) || memberId <= 0) return false;
  if (asked.has(memberId)) return false;
  asked.add(memberId);

  Promise.resolve(enrolIfEligible({ store }))
    .then(() => {
      // Seal what they wrote BEFORE this, the way the other two triggers do —
      // just not on the same tick as everyone else in the room. For whoever
      // holds the key after the attempt, NOT only for a fresh `ENROLLED`: a
      // member who enrols by typing and never opens the Hub used to get one
      // attempt ever, on the enrolling keystroke, and if it landed on
      // `OUTBOX_PENDING` or no network their old rows stayed plaintext for
      // good. Cheap once migrated, single-flight while it runs, not awaited.
      scheduleMigration({ store });
    })
    .catch(error => {
      // An unexpected throw. Expected failures come back as outcomes, and a
      // member who cannot enrol writes plaintext exactly as they did before.
      console.warn('[CoursesE2EE] Silent enrolment from a blank failed:', error);
    });
  return true;
}
